import React, { Suspense } from 'react';
import { Header } from './components/layout';
import { Footer } from './components/layout';
import {
  Hero,
  MenuSection,
  WineSection,
  ReviewsSection,
  GallerySection,
  LocationSection,
  SocialSection,
  ImpressumSection,
  ReservierungSection,
  OeffnungszeitenSection,
} from './components/sections';
import contentData from './data/content.json';

/**
 * Loading fallback for Suspense boundaries
 */
const SectionFallback: React.FC = () => (
  <div className="min-h-[200px] flex items-center justify-center bg-[#1a1a1a]">
    <div className="w-8 h-8 border-2 border-[#c9a84c] border-t-transparent rounded-full animate-spin" aria-label="Lädt..." />
  </div>
);

/**
 * App Component
 *
 * Root component that composes the full single-page restaurant website.
 * Renders all sections in order with a sticky header and footer.
 *
 * Section order (matching Navigation NAV_LINKS):
 *   #home        → Hero
 *   #speisekarte → MenuSection
 *   #weine       → WineSection
 *   #bewertungen → ReviewsSection
 *   #galerie     → GallerySection
 *   #standort    → LocationSection
 *   #social      → SocialSection
 *   #impressum   → ImpressumSection
 *
 * Requirements:
 * - 2.1: Navigation links to all sections
 * - 2.2: Smooth scroll behaviour
 * - 2.5: Active section tracking via IntersectionObserver (handled in Navigation)
 * - 11.3: Lazy loading for below-fold content
 */
const App: React.FC = () => {
  const { contact } = contentData;

  return (
    <>
      {/* Skip to main content link for keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[9999] focus:px-4 focus:py-2 focus:bg-[#c9a84c] focus:text-[#1a1a1a] focus:font-semibold focus:rounded-lg focus:shadow-lg focus:outline-none"
      >
        Zum Hauptinhalt springen
      </a>

      {/* Sticky header with logo and navigation */}
      <header>
        <Header />
      </header>

      <main id="main-content" aria-label="Hauptinhalt">
        {/* Hero — uses its own <section id="home"> — above fold, no lazy loading */}
        <Hero />

        {/* Below-fold sections wrapped in Suspense for lazy loading support */}
        <Suspense fallback={<SectionFallback />}>
          {/* Menu — <section id="speisekarte"> */}
          <MenuSection />

          {/* Wine — <section id="weine"> */}
          <WineSection />

          {/* Reservierung — <section id="reservierung"> */}
          <ReservierungSection />

          {/* Reviews — <section id="bewertungen"> */}
          <ReviewsSection />

          {/* Öffnungszeiten — <section id="oeffnungszeiten"> */}
          <OeffnungszeitenSection />

          {/* Gallery — <section id="galerie"> */}
          <GallerySection />

          {/* Location / Map — <section id="standort"> */}
          <LocationSection />

          {/* Social Media — <section id="social"> */}
          <SocialSection />

          {/* Impressum — <section id="impressum"> */}
          <ImpressumSection />
        </Suspense>
      </main>

      {/* Footer with contact info */}
      <footer>
        <Footer
          phone={contact.phone || undefined}
          email={contact.email || undefined}
          openingHours={contact.openingHours || []}
        />
      </footer>
    </>
  );
};

export default App;
